import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { useToast, humanizeError } from '../shared/Toast'

const MAX_BYTES = 10 * 1024 * 1024
const BOTTLE = 'bottle.jpg'
const PRESCRIPTION_ACCEPT = 'application/pdf,image/jpeg,image/png'

/**
 * Bottle photos, one per medication, plus a prescription upload for the list
 * as a whole.
 *
 * There is no photo column on `medications`, and this session must not alter
 * existing tables, so "does this medication have a photo?" is answered by
 * listing the storage folder rather than reading a flag.
 */
export default function MedicationPhotos({ recipientId, meds, canManage, onUploaded }) {
  const { user } = useAuth()
  const toast = useToast()
  const [thumbs, setThumbs] = useState({}) // medicationId -> signed url
  const [busyId, setBusyId] = useState(null)
  const [rxBusy, setRxBusy] = useState(false)

  const loadThumbs = useCallback(async () => {
    if (!meds.length) return
    const next = {}
    await Promise.all(meds.map(async (m) => {
      const dir = `${recipientId}/medications/${m.id}`
      const { data } = await supabase.storage.from('care-photos').list(dir)
      if (data?.some((f) => f.name === BOTTLE)) {
        const { data: signed } = await supabase.storage
          .from('care-photos')
          .createSignedUrl(`${dir}/${BOTTLE}`, 3600)
        if (signed?.signedUrl) next[m.id] = signed.signedUrl
      }
    }))
    setThumbs(next)
  }, [recipientId, meds])

  useEffect(() => { loadThumbs() }, [loadThumbs])

  async function uploadBottle(med, file) {
    if (!file) return
    if (!file.type.startsWith('image/')) return toast.error('Please choose an image.')
    if (file.size > MAX_BYTES) return toast.error('Image must be under 10MB.')
    setBusyId(med.id)
    try {
      // Fixed filename so re-uploading replaces rather than accumulating. The
      // stored content type comes from the file itself, so a PNG saved under
      // this name still renders correctly.
      const path = `${recipientId}/medications/${med.id}/${BOTTLE}`
      const { error } = await supabase.storage
        .from('care-photos')
        .upload(path, file, { upsert: true, contentType: file.type })
      if (error) throw error
      const { data: signed } = await supabase.storage.from('care-photos').createSignedUrl(path, 3600)
      if (signed?.signedUrl) setThumbs((t) => ({ ...t, [med.id]: signed.signedUrl }))
      toast.success('Photo saved.')
    } catch (err) {
      toast.error(humanizeError(err))
    } finally {
      setBusyId(null)
    }
  }

  async function uploadPrescription(file) {
    if (!file) return
    if (file.size > MAX_BYTES) return toast.error('File must be under 10MB.')
    setRxBusy(true)
    try {
      const ext = file.name.split('.').pop()
      const path = `${recipientId}/prescriptions/rx-${Date.now()}.${ext}`
      const { error: upErr } = await supabase.storage.from('documents').upload(path, file, { upsert: false })
      if (upErr) throw upErr
      // Recorded as a document so it also shows on the Documents page.
      const { error: rowErr } = await supabase.from('documents').insert({
        care_recipient_id: recipientId,
        uploaded_by: user.id,
        file_name: file.name,
        file_url: path,
        file_size_bytes: file.size,
        category: 'prescription',
        description: 'Medication prescription',
      })
      if (rowErr) throw rowErr
      // TODO: wire to Claude vision edge function for medication extraction
      toast.success('Prescription uploaded.')
      onUploaded?.()
    } catch (err) {
      toast.error(humanizeError(err))
    } finally {
      setRxBusy(false)
    }
  }

  return (
    <>
      {meds.map((m) => (
        <div key={m.id} className="spread" style={{ paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>
          <div className="row" style={{ gap: 10, minWidth: 0 }}>
            {thumbs[m.id] ? (
              <img
                src={thumbs[m.id]}
                alt={`${m.name} bottle`}
                style={{ width: 38, height: 38, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
              />
            ) : (
              <div
                aria-hidden
                style={{
                  width: 38, height: 38, borderRadius: 8, flexShrink: 0,
                  background: 'var(--teal-light)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: 16,
                }}
              >
                💊
              </div>
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600 }}>{m.name}</div>
              <div className="muted" style={{ fontSize: 13 }}>
                {[m.dose, m.timing].filter(Boolean).join(' · ')}
              </div>
            </div>
          </div>

          <div className="row" style={{ gap: 8, flexShrink: 0 }}>
            {m.purpose && <span className="badge">{m.purpose}</span>}
            {canManage && (
              <label
                title={thumbs[m.id] ? 'Replace bottle photo' : 'Add bottle photo'}
                style={{ cursor: 'pointer', fontSize: 18, lineHeight: 1, opacity: busyId === m.id ? 0.4 : 1 }}
              >
                {busyId === m.id ? <span className="spinner" /> : '📷'}
                <input
                  type="file" accept="image/*" capture="environment" hidden
                  disabled={busyId === m.id}
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; uploadBottle(m, f) }}
                  aria-label={`Photo of ${m.name} bottle`}
                />
              </label>
            )}
          </div>
        </div>
      ))}

      {canManage && (
        <label
          className="card card-tap"
          style={{
            display: 'block', cursor: 'pointer', marginTop: 4,
            border: '1.5px dashed var(--border)', textAlign: 'center',
            color: 'var(--teal)', fontWeight: 600, fontSize: 14, padding: 14,
          }}
        >
          {rxBusy ? <span className="spinner" /> : '+ Upload prescription or medication list'}
          <input
            type="file" accept={PRESCRIPTION_ACCEPT} hidden disabled={rxBusy}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; uploadPrescription(f) }}
          />
        </label>
      )}
    </>
  )
}
